import React, { useEffect, useRef, useState } from "react";
import { useLocation, Link, useNavigate } from "react-router-dom";
import { InfoIcon, LayoutGrid, LayoutList } from "lucide-react";
import Switch from "@mui/material/Switch";
import { useLayoutContext } from "./context/LayoutContext";
import { LoginWidget } from "./components/LoginWidget";
import { useAuth } from "./context/AuthContext";
import Image from "./components/common/Image";
import ErrorMessage from "./components/common/ErrorMessage";
import SuccessMessage from "./components/common/SuccessMessage";
import LoadingScreen from "./components/common/LoadingScreen";
import ErrorScreen from "./components/common/ErrorScreen";
import ModalScreen from "./components/common/ModalScreen";
import ErrorBoundary from "./components/common/ErrorBoundary";

interface SystemMessageProps {
  message: string | null;
  onClick?: () => void;
}

const Layout = ({ children }: { children: React.ReactNode }) => {
  const { layoutListItem, setLayoutListItem } = useLayoutContext();
  const navigate = useNavigate();
  const { pathname, state } = useLocation();
  const { isAuthenticated, profile, profileData } = useAuth();
  const { tasks, addTask, removeTask } = useLayoutContext();
  const {
    userSettings,
    setUserSettings,
    systemError,
    setSystemError,
    loading,
    setLoading,
    error,
    setError,
    success,
    setSuccess,
    modalScreen,
    setModalScreen,
    page,
    setPage,
    modalRef,
  } = useLayoutContext();
  const [resellerView, _setResellerView] = useState<boolean>(false);
  // Every distinct modal object gets a fresh ModalScreen instance. This avoids
  // stale local state (e.g. an internal "closed" state) when a close and a new
  // open are batched into the same commit. A WeakMap keeps the key stable
  // across repeated renders of the same modal object (StrictMode/concurrent).
  const modalKeysRef = useRef(new WeakMap<object, number>());
  const nextModalKeyRef = useRef(0);
  const [systemMessage, setSystemMessage] = useState<SystemMessageProps | null>(
    null,
  );
  const [profileError, setProfileError] = useState(false);

  const fetchProfile = async () => {
    setProfileError(false);
    try {
      const profileData = await profile();
      setUserSettings(profileData ?? null);
    } catch {
      // Keep the last known settings (graceful degradation). The fetch layer
      // already reports non-lifecycle failures via reportError.
      setProfileError(true);
    }
  };

  const setResellerView = (view: boolean) => {
    _setResellerView(view);
    localStorage.setItem("resellerView", JSON.stringify(view));
  };

  useEffect(() => {
    const storedView = localStorage.getItem("resellerView");
    if (storedView) {
      setResellerView(JSON.parse(storedView));
    }
  }, []);

  useEffect(() => {
    if (isAuthenticated()) {
      fetchProfile();
    } else {
      navigate("/login", { state: { from: pathname } });
    }
  }, [profile, isAuthenticated, profileData]);

  useEffect(() => {
    const handlePopState = () => {
      const urlParams = new URLSearchParams(window.location.search);
      const pageParam = urlParams.get("page") ?? "1";
      if (pageParam) {
        setPage(Number(pageParam));
      }
    };

    handlePopState();
    window.addEventListener("popstate", handlePopState);
    return () => {
      window.removeEventListener("popstate", handlePopState);
    };
  }, []);

  useEffect(() => {
    if (state?.infoMsg) {
      setSystemMessage({
        message: state.infoMsg,
        onClick: () => {
          setSystemMessage(null);
          navigate(state.from || "/");
        },
      });
    } else if (
      userSettings?.profile.isCredentialsExpired &&
      userSettings.isOperatorUser()
    ) {
      setSystemMessage({
        message:
          "As suas credenciais expiraram. Por favor, atualize a sua senha.",
        onClick: () => {
          navigate("/settings");
        },
      });
    } else {
      setSystemMessage(null);
    }
  }, [userSettings, state]);

  useEffect(() => {
    tasks.forEach((task) => {
      const url = new URL(window.location.href);
      const currentModule = url.pathname.split("/")[1] || "store";
      if (task.allowedModules.length) {
        const hasAccess = task.allowedModules.some(
          (module) => module == currentModule,
        );
        if (!hasAccess) {
          if (
            confirm(
              "Tem uma proposta aberta. Pretende fechá-la antes de continuar?",
            )
          ) {
            removeTask(task.id);
          } else {
            history.go(-1);
          }
        }
      }
    });
    // Only re-evaluate when the open tasks or the route change; running this on
    // every render re-opened the native confirm / popped history repeatedly.
  }, [tasks, pathname]); // eslint-disable-line react-hooks/exhaustive-deps

  const renderTaskTip = (task: any) => {
    const url = new URL(window.location.href);
    const currentModule = url.pathname.split("/")[1] || "store";
    if (task.tip[currentModule]) {
      return " - " + task.tip[currentModule];
    }
    return "";
  };

  const renderTasks = () => {
    if (tasks.length === 0) {
      return null;
    }
    return (
      <div className="tasks container mx-auto px-4 py-2 notification is-info not-printable">
        {tasks.map((task) => (
          <div
            key={task.id}
            className="task-item flex items-center space-x-2 is-justify-content-center p-2"
          >
            <span
              className="task-name text-xl font-extrabold"
              onClick={() => {
                if (task.onSwitch) {
                  task.onSwitch();
                }
              }}
            >
              {task.name}
            </span>
            <span
              className="task-tip text-sm italic text-gray-100 cursor-pointer"
              onClick={() => {
                if (task.onSwitch) {
                  task.onSwitch();
                }
              }}
            >
              {renderTaskTip(task)}
            </span>
            <button
              onClick={() => removeTask(task.id)}
              className="task-close-button text-red-900 hover:text-gray-700 font-bold text-xl ml-4"
            >
              &times;
            </button>
          </div>
        ))}
      </div>
    );
  };

  const renderSystemMessage = () => {
    if (systemMessage && systemMessage.message) {
      return (
        <div
          className={`bg-blue-100 text-blue-800 p-4 text-center cursor-pointer`}
          onClick={systemMessage.onClick}
        >
          <InfoIcon className="inline-block mr-2 w-5 h-5" />
          {systemMessage.message}
        </div>
      );
    }
    return null;
  };

  const renderLoadingScreen = () => {
    if (loading) {
      return <LoadingScreen message={loading.message} style={loading.style} />;
    }
    return null;
  };

  const renderModalScreen = () => {
    if (modalScreen) {
      let modalKey = modalKeysRef.current.get(modalScreen);
      if (!modalKey) {
        modalKey = ++nextModalKeyRef.current;
        modalKeysRef.current.set(modalScreen, modalKey);
      }
      return (
        <ModalScreen
          key={modalKey}
          ref={modalRef}
          className={modalScreen.className}
          title={modalScreen.title}
          state={modalScreen.state}
          onClose={modalScreen.onClose}
          onShow={modalScreen.onShow}
          footer={modalScreen.footer}
          childrenControlOverflow={modalScreen.childrenControlOverflow}
          dismissOnEscape={modalScreen.dismissOnEscape}
        >
          <ErrorBoundary>{modalScreen.children}</ErrorBoundary>
        </ModalScreen>
      );
    }
    return null;
  };

  const renderSuccessMessage = () => {
    if (success) {
      return (
        <SuccessMessage message={success} onClose={() => setSuccess(null)} />
      );
    }
    return null;
  };

  const renderErrorMessage = () => {
    if (error) {
      return <ErrorMessage message={error} onClose={() => setError(null)} />;
    }
    return null;
  };

  if (!["/login", "/register", "/reset-password"].includes(pathname)) {
    if (!isAuthenticated()) {
      return (
        <div className="flex items-center justify-center h-screen">
          <h1 className="text-2xl font-bold">Acesso Negado</h1>
        </div>
      );
    }
    if (isAuthenticated() && !userSettings) {
      if (profileError) {
        return (
          <div className="flex flex-col items-center justify-center h-screen gap-3">
            <p className="text-lg font-medium text-gray-800">
              Não foi possível carregar a sessão.
            </p>
            <button
              type="button"
              onClick={() => void fetchProfile()}
              className="px-4 py-2 rounded-md bg-blue-600 text-white hover:bg-blue-700 transition"
            >
              Tentar novamente
            </button>
          </div>
        );
      }
      return (
        <div className="flex items-center justify-center h-screen">
          Loading...
        </div>
      );
    }
  }

  if (systemError) {
    return (
      <ErrorScreen
        message={systemError.message}
        details={systemError.details}
        retryAfter={systemError.retryAfter}
        onClose={() => setSystemError(null)}
      />
    );
  }
  return (
    <div
      className={`min-h-screen bg-gray-100${resellerView ? " reseller-view" : ""}`}
    >
      <header className="bg-white shadow not-printable">
        <div className="container mx-auto px-4 py-4 flex justify-between items-center">
          <h1 className="text-2xl font-bold">
            <Link to="/" className="text-gray-800 hover:text-blue-500">
              <div className="image-container  max-w-[150px] max-h-[100px]">
                <Image
                  src="http://iapc.pt/images/150/logo-IAPC.jpg"
                  dataSrc={userSettings?.generalSettings?.company_logo_location}
                  loading="progressive"
                  sizes="150"
                  alt="Logo"
                  className="object-cover w-full h-10"
                />
              </div>
            </Link>
          </h1>
          <nav className="navbar">
            <div className="navbar-item px-4 py-2 is-hidden">
              {layoutListItem !== "grid" && (
                <button
                  className={`text-white hover:bg-gray-700 rounded`}
                  onClick={() => setLayoutListItem("grid")}
                >
                  <LayoutGrid className="text-gray-400 w-8 h-8" />
                </button>
              )}
              {layoutListItem !== "list" && (
                <button
                  className={`text-white hover:bg-gray-700 rounded`}
                  onClick={() => setLayoutListItem("list")}
                >
                  <LayoutList className="text-gray-400 w-8 h-8" />
                </button>
              )}
            </div>
            {isAuthenticated() && userSettings?.isOperatorUser() && (
              <div className="navbar-item px-4 py-2">
                <Switch
                  value={resellerView}
                  checked={resellerView}
                  inputProps={{ "aria-label": "Modo reseller" }}
                  onClick={(e) => {
                    e.preventDefault();
                    setResellerView(!resellerView);
                  }}
                  disabled={
                    !userSettings?.generalSettings?.show_margin_slider &&
                    !userSettings?.generalSettings?.show_cost_price
                  }
                  className={``}
                />
              </div>
            )}
            {
              // if page is not /login, /register, or /reset-password hide the login widget
              !["/login", "/register", "/reset-password"].includes(
                pathname,
              ) && (
                <div className="navbar-item px-4 py-2">
                  <LoginWidget
                    isAuthenticated={isAuthenticated() || false}
                    userSettings={userSettings}
                  />
                </div>
              )
            }
          </nav>
        </div>
        {renderTasks()}
      </header>

      {renderSystemMessage()}
      {renderLoadingScreen()}

      <main className="h-min-screen h-full">{children}</main>

      {renderErrorMessage()}
      {renderSuccessMessage()}
      {renderModalScreen()}
    </div>
  );
};
export default Layout;
